"use client";

import React, { useState } from "react";
import Link from "next/link";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ArrowRight, Info, Mail } from "lucide-react";
import { handleForgotEmail } from "@/api/auth";
import { validateIsEmail } from "@/hooks/types";
import BrandInput from "../inputs/BrandInput";
import { cn, display } from "@/lib/utils";

const ResetPassEmailForm: React.FC = (): React.JSX.Element => {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  // Kept apart from mutation.isSuccess so "Reenviar" doesn't flash the form while pending.
  const [sentTo, setSentTo] = useState<string | null>(null);

  const mutation = useMutation({
    mutationKey: ["sendForgotPassEmail"],
    mutationFn: handleForgotEmail,
    onSuccess: (_data, sentEmail) => setSentTo(sentEmail),
  });

  const emailError = touched && email !== "" && !validateIsEmail(email) ? "Escribe un correo válido" : undefined;
  const canSubmit = validateIsEmail(email) && !mutation.isPending;

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    mutation.mutate(email.trim());
  };

  const serverError = mutation.isError
    ? (isAxiosError<{ message?: string }>(mutation.error) && mutation.error.response?.data?.message) ||
      "No pudimos enviar el enlace. Inténtalo de nuevo."
    : null;

  if (sentTo) {
    return (
      <div className="text-brand-ink">
        <div className="w-14 h-14 bg-brand-blue text-white flex items-center justify-center">
          <Mail className="w-6 h-6" />
        </div>
        <h1 className="mes-display text-[clamp(1.75rem,4.5vw,3rem)] leading-[0.95] mt-7">{display("Revisa tu correo")}</h1>
        <p className="font-body text-base leading-relaxed text-brand-ink/70 mt-5">
          Te enviamos un enlace a <strong className="text-brand-ink font-bold break-all">{sentTo}</strong>. Ábrelo para
          crear tu nueva contraseña.
        </p>

        <div className="mt-8 border border-brand-blue bg-brand-blue/5 px-4 py-3 flex gap-2.5 items-start text-brand-blue font-body text-sm leading-relaxed">
          <Info className="w-[18px] h-[18px] shrink-0 mt-px" />
          <span>¿No te llegó? Revisa la carpeta de spam o promociones.</span>
        </div>

        <div className="mt-9 flex gap-3">
          <Link
            href="/login"
            className="flex-1 flex items-center justify-between mes-label px-5 py-4 bg-brand-ink text-white hover:bg-brand-blue transition-colors"
          >
            <span>Volver a iniciar sesión</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            type="button"
            onClick={() => submit()}
            disabled={mutation.isPending}
            className="mes-label px-5 py-4 border border-brand-ink hover:bg-brand-ink hover:text-white transition-colors disabled:opacity-50"
          >
            {mutation.isPending ? "Enviando…" : "Reenviar"}
          </button>
        </div>

        {serverError && (
          <div role="alert" className="mt-6 border border-brand-red bg-brand-red/5 px-4 py-3 mes-label text-brand-red normal-case tracking-normal">
            {serverError}
          </div>
        )}

        <p className="mes-label mt-10 opacity-60">
          ¿Correo equivocado?{" "}
          <button type="button" onClick={() => {
              setSentTo(null);
              mutation.reset();
            }} className="text-brand-blue hover:underline uppercase">
            Cámbialo →
          </button>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="text-brand-ink">
      <p className="mes-label bg-brand-yellow inline-block px-3 py-1.5">Paso 1 de 2</p>
      <h1 className="mes-display text-[clamp(1.75rem,4.5vw,3rem)] leading-[0.95] mt-6">{display("Recupera tu contraseña")}</h1>
      <p className="font-body text-base leading-relaxed text-brand-ink/70 mt-5">
        Escribe el correo con el que te registraste y te enviaremos un enlace para crear una nueva.
      </p>

      <div className="mt-11">
        <BrandInput
          label="Correo electrónico"
          type="email"
          value={email}
          onChange={(v) => {
            setEmail(v);
            if (mutation.isError) mutation.reset();
          }}
          placeholder="tu@correo.com"
          autoComplete="email"
          error={emailError}
        />
      </div>

      {serverError && (
        <div role="alert" className="mt-6 border border-brand-red bg-brand-red/5 px-4 py-3 mes-label text-brand-red normal-case tracking-normal">
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className={cn(
          "mt-9 w-full flex items-center justify-between mes-label px-5 py-4 transition-colors",
          "bg-brand-ink text-white hover:bg-brand-blue",
          "disabled:bg-brand-ink/20 disabled:text-brand-ink/50 disabled:cursor-not-allowed"
        )}
      >
        <span>{mutation.isPending ? "Enviando…" : "Enviar enlace"}</span>
        <ArrowRight className={cn("w-4 h-4", mutation.isPending && "animate-pulse")} />
      </button>

      <p className="mes-label mt-10 opacity-60">
        ¿Ya la recordaste?{" "}
        <Link href="/login" className="text-brand-blue hover:underline">
          Inicia sesión →
        </Link>
      </p>
    </form>
  );
};

export default ResetPassEmailForm;
